import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { memo, useState, useCallback } from 'react';
import type { RSVP } from '@penticton-dev-events/shared';

export type PendingRSVPsListProps = {
  readonly rsvps: RSVP[];
  readonly approvingIds: Set<string>;
  readonly rejectingIds: Set<string>;
  readonly batchApproving: boolean;
  readonly onApprove: (userId: string) => Promise<void>;
  readonly onReject: (userId: string, reason?: string) => Promise<void>;
  readonly onBatchApprove: (userIds: string[]) => Promise<void>;
};

/**
 * Displays pending RSVPs with approve/reject actions and batch approval
 */
const PendingRSVPsList = memo<PendingRSVPsListProps>(
  ({
    rsvps,
    approvingIds,
    rejectingIds,
    batchApproving,
    onApprove,
    onReject,
    onBatchApprove,
  }) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [actionError, setActionError] = useState<string | null>(null);

    const handleToggleSelect = useCallback((userId: string) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(userId)) {
          next.delete(userId);
        } else {
          next.add(userId);
        }
        return next;
      });
    }, []);

    const handleSelectAll = useCallback(() => {
      if (selectedIds.size === rsvps.length) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(rsvps.map(rsvp => rsvp.userId)));
      }
    }, [rsvps, selectedIds.size]);

    const handleApprove = useCallback(
      async (userId: string) => {
        setActionError(null);
        try {
          await onApprove(userId);
          // Remove from selected if it was selected
          setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
        } catch (err) {
          setActionError(err instanceof Error ? err.message : 'Failed to approve RSVP');
        }
      },
      [onApprove],
    );

    const handleReject = useCallback(
      async (userId: string) => {
        setActionError(null);
        // TODO: Add dialog for rejection reason
        try {
          await onReject(userId);
          // Remove from selected if it was selected
          setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
        } catch (err) {
          setActionError(err instanceof Error ? err.message : 'Failed to reject RSVP');
        }
      },
      [onReject],
    );

    const handleBatchApprove = useCallback(async () => {
      if (selectedIds.size === 0) {
        return;
      }

      setActionError(null);
      try {
        await onBatchApprove(Array.from(selectedIds));
        setSelectedIds(new Set());
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to batch approve RSVPs');
      }
    }, [selectedIds, onBatchApprove]);

    if (rsvps.length === 0) {
      return (
        <Alert severity="info">
          No pending RSVPs. All attendees have been reviewed.
        </Alert>
      );
    }

    return (
      <Stack spacing={2}>
        {/* Batch Actions */}
        {rsvps.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Checkbox
              checked={selectedIds.size === rsvps.length && rsvps.length > 0}
              indeterminate={selectedIds.size > 0 && selectedIds.size < rsvps.length}
              onChange={handleSelectAll}
            />
            <Typography variant="body2" color="text.secondary">
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : 'Select all'}
            </Typography>
            {selectedIds.size > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleBatchApprove}
                disabled={batchApproving}
                size="small"
              >
                {batchApproving
                  ? 'Approving...'
                  : `Approve ${selectedIds.size}`}
              </Button>
            )}
          </Stack>
        )}

        {/* Error Alert */}
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        {/* RSVP List */}
        <Stack spacing={1}>
          {rsvps.map(rsvp => {
            const isApproving = approvingIds.has(rsvp.userId);
            const isRejecting = rejectingIds.has(rsvp.userId);
            const isProcessing = isApproving || isRejecting;
            const isSelected = selectedIds.has(rsvp.userId);

            return (
              <Card key={rsvp.userId} variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {/* Selection Checkbox */}
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleSelect(rsvp.userId)}
                      disabled={isProcessing}
                    />

                    {/* User Avatar */}
                    <Avatar
                      src={rsvp.userPhotoURL}
                      alt={rsvp.userDisplayName}
                      sx={{ width: 40, height: 40 }}
                    >
                      {rsvp.userPhotoURL ? null : <PersonIcon />}
                    </Avatar>

                    {/* User Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {rsvp.userDisplayName}
                      </Typography>
                      {rsvp.userEmail && (
                        <Typography variant="caption" color="text.secondary">
                          {rsvp.userEmail}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Requested: {new Date(rsvp.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>

                    {/* Status Badge */}
                    <Chip
                      label="Pending"
                      color="warning"
                      size="small"
                      variant="outlined"
                    />

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Approve">
                        <IconButton
                          color="success"
                          onClick={() => handleApprove(rsvp.userId)}
                          disabled={isProcessing}
                          size="small"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          color="error"
                          onClick={() => handleReject(rsvp.userId)}
                          disabled={isProcessing}
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    );
  },
);

PendingRSVPsList.displayName = 'PendingRSVPsList';

export default PendingRSVPsList;
